// ---------- la pub de pause ----------
// Une interstitielle au retour au catalogue, après une vraie séance de coloriage — en plus de
// celle qui suit une feuille terminée (cf. src/ads.ts), et avec la même règle d'or : jamais au
// milieu d'un geste, seulement quand le joueur a LUI-MÊME décidé de lever le nez de la feuille.
//
// POURQUOI elle existe. L'interstitielle de fin ne tombe presque jamais : une feuille compte de
// 7 500 à 60 000 cases (cf. shared/difficulty.ts), elle se colorie sur plusieurs soirées, et
// l'essentiel des sessions s'achève donc sur une feuille EN COURS. Restait la bannière, qui au
// Brésil — là où la campagne amène les joueurs — rapportait quelques centimes pour une
// cinquantaine d'installations (septembre 2026). Le levier n'était pas d'ajouter un format mais
// de donner à celui qui paie le mieux un moment où il tombe réellement.
//
// Ce qui la rend supportable, et c'est tout le module :
// - du temps de coloriage ACTIF, pas du temps d'écran : une feuille ouverte et oubliée sur la
//   table ne « mérite » pas de pub, et le joueur qui revient d'une pause café non plus ;
// - un compteur remis à zéro à chaque interstitielle MONTRÉE, celle de fin comprise, si bien que
//   deux pubs sont toujours séparées d'au moins MIN_PLAY_MS de jeu ;
// - rien du tout pendant les premiers jours : le nouveau venu découvre le jeu sans être coupé,
//   et c'est lui que la campagne paie le plus cher d'aller chercher.
//
// Ce module ne touche pas au SDK : il compte et il décide, src/ads.ts charge et affiche. C'est ce
// qui permet de tester la règle sans téléphone (tests/adPacing.test.ts).

/** Cinq minutes de coloriage. En dessous, c'est un aller-retour — vérifier une autre feuille,
 *  changer d'avis — et une pub là punirait le fait de naviguer. */
export const MIN_PLAY_MS = 5 * 60 * 1000;

/** Au-delà de trente secondes sans poser une case, l'intervalle n'est plus du jeu : le joueur
 *  cherche, regarde, ou a posé le téléphone. On n'en compte que les trente premières secondes. */
export const IDLE_CAP_MS = 30 * 1000;

/** Deux jours de grâce depuis la première ouverture. Assez pour découvrir le jeu et revenir au
 *  moins une fois ; la bannière et l'interstitielle de fin, elles, restent actives dès le premier
 *  jour, comme avant. */
export const GRACE_MS = 2 * 24 * 60 * 60 * 1000;

/** Une interstitielle chargée expire au bout d'une heure côté Google, et `showInterstitial()`
 *  échoue alors en silence. Quarante-cinq minutes après une demande restée sans affichage — le
 *  joueur a continué de colorier sans quitter la feuille — on en redemande une fraîche. */
export const REARM_MS = 45 * 60 * 1000;

/** Temps de jeu accumulé depuis la dernière interstitielle montrée. En mémoire seulement :
 *  relancer l'application repart de zéro, ce qui n'avantage que le joueur. */
let playMs = 0;
/** Instant de la dernière case posée, sur l'horloge de l'appelant (performance.now). */
let lastFillAt = -Infinity;
/** Instant de la dernière demande de préchargement, même horloge. -Infinity : rien de demandé
 *  depuis la dernière pub, ou la demande n'a pas abouti à un affichage (cf. rearm). */
let requestedAt = -Infinity;

/**
 * Une case vient d'être posée. Appelé par fillCell() — donc une fois PAR CASE d'un remplissage de
 * zone, parfois des milliers d'un coup : c'est pourquoi il ne fait que quelques comparaisons. Les
 * cases d'une même zone tombent au même instant et n'ajoutent rien.
 *
 * Rend vrai quand il est temps de PRÉCHARGER : le seuil vient d'être franchi, ou la pub demandée
 * plus tôt a dû expirer. Précharger pendant que le joueur colorie encore, c'est ne jamais le
 * faire attendre à la porte.
 */
export function notePlay(now: number): boolean {
  const gap = now - lastFillAt;
  lastFillAt = now;
  // `isFinite` écarte la toute première case de la session : aucune case avant elle, donc aucun
  // intervalle — sans ce garde, elle comptait d'office trente secondes de jeu.
  if (gap > 0 && Number.isFinite(gap)) playMs += Math.min(gap, IDLE_CAP_MS);
  if (playMs < MIN_PLAY_MS || now - requestedAt < REARM_MS) return false;
  requestedAt = now;
  return true;
}

/** Une interstitielle vient d'être montrée — de pause ou de fin. Tout repart de zéro. */
export function resetPlayClock(): void {
  playMs = 0;
  requestedAt = -Infinity;
}

/** Le joueur a quitté la feuille alors que la pub était due mais pas prête (réseau, aucune pub à
 *  servir). La prochaine case posée en redemandera une, au lieu d'attendre REARM_MS. */
export function rearm(): void {
  requestedAt = -Infinity;
}

/** L'installation est-elle sortie de sa période de grâce ? Séparé de breakAdDue() parce que le
 *  préchargement la consulte aussi : charger une pub qu'on ne montrera pas fait baisser le taux
 *  d'affichage, qu'AdMob regarde. */
export function pastGrace(now: number, installedAt: number): boolean {
  return now - installedAt >= GRACE_MS;
}

/** Faut-il une pub de pause en quittant la feuille ? La fréquence est tenue par le compteur
 *  lui-même, remis à zéro à chaque pub montrée. */
export function breakAdDue(now: number, installedAt: number): boolean {
  return pastGrace(now, installedAt) && playMs >= MIN_PLAY_MS;
}
